import { components } from "@/__openapi__/backend/schema";
import type { AssertTrue, ValidateEnumIsSubtype } from "@/domains/typing/types";
import { SessionPartContext } from "@/mds/components/sessions/session-parts/session-part.types";

/**
 * Session Types
 */

export type SessionEvent = components["schemas"]["SessionEventSummaryResponseSchema"];
export type AgentActionV1SessionEvent =
  components["schemas"]["SessionEventAgentActionV1EventDataSchema"];
export type AgentPlanningV1SessionEvent =
  components["schemas"]["SessionEventAgentPlanningV1EventDataSchema"];
export type UserActionV1SessionEvent =
  components["schemas"]["SessionEventUserActionV1EventDataSchema"];
export type SystemNotificationV1SessionEvent =
  components["schemas"]["SessionEventSystemNotificationV1EventDataSchema"];

export type MarkdownV1SessionPart = components["schemas"]["MarkdownV1SessionPart"];
export type ReasoningV1SessionPart = components["schemas"]["ReasoningV1SessionPart"];
export type WebClipV1SessionPart = components["schemas"]["WebClipV1SessionPart"];
export type ArtifactV1SessionPart = components["schemas"]["ArtifactSessionPartV1"];

export type NoteCreatedV1ArtifactPayload = components["schemas"]["NoteCreatedV1ArtifactPayload"];
export type NoteUpdatedV1ArtifactPayload = components["schemas"]["NoteUpdatedV1ArtifactPayload"];

export type SessionArtifactPayload = NoteCreatedV1ArtifactPayload | NoteUpdatedV1ArtifactPayload;

export type SessionEventData = SessionEvent["data"];
export type SessionEventKindValues = SessionEvent["data"]["kind"];

// The includes all of our older/deprecated parts.
export type UnknownSessionPart =
  | AgentActionV1SessionEvent["value"]["parts"][number]
  | AgentPlanningV1SessionEvent["value"]["parts"][number]
  | UserActionV1SessionEvent["value"]["parts"][number]
  | SystemNotificationV1SessionEvent["value"]["parts"][number];

export type AnySessionPart =
  | MarkdownV1SessionPart
  | ReasoningV1SessionPart
  | WebClipV1SessionPart
  | ArtifactV1SessionPart
  | UnknownSessionPart;

export enum SessionEventKind {
  AgentActionV1 = "AGENT_ACTION_V1",
  AgentPlanningV1 = "AGENT_PLANNING_V1",
  UserActionV1 = "USER_ACTION_V1",
  SystemNotificationV1 = "SYSTEM_NOTIFICATION_V1",
}

type _ValidateSessionEventKindValues = AssertTrue<
  ValidateEnumIsSubtype<SessionEventKind, SessionEventKindValues>
>;

export type SessionPartKindValues = NonNullable<AnySessionPart["kind"]>;

export enum SessionPartKind {
  MarkdownV1 = "MARKDOWN_V1",
  ReasoningV1 = "REASONING_V1",
  WebClipV1 = "WEB_CLIP_V1",
  ArtifactV1 = "ARTIFACT_V1",
}

type _ValidateSessionPartKindValues = AssertTrue<
  ValidateEnumIsSubtype<SessionPartKind, SessionPartKindValues>
>;

export type ArtifactV1SessionPartKindValues = ArtifactV1SessionPart["artifact_kind"];

export enum ArtifactV1SessionPartKind {
  NoteCreatedV1 = "NOTE_CREATED_V1",
  NoteUpdatedV1 = "NOTE_UPDATED_V1",
}

type _ValidateArtifactV1SessionPartKindValues = AssertTrue<
  ValidateEnumIsSubtype<ArtifactV1SessionPartKind, ArtifactV1SessionPartKindValues>
>;

export type SessionStatusValues = components["schemas"]["SessionStatus"];

export type SessionData = components["schemas"]["SessionResponseSchema"];

export enum SessionStatus {
  Waiting = "WAITING",
  Processing = "PROCESSING",
  Finished = "FINISHED",
  Error = "ERROR",
  Dismissed = "DISMISSED",
  Abandoned = "ABANDONED",
  Unknown = "UNKNOWN",
}

type _ValidateSessionStatusValues = AssertTrue<
  ValidateEnumIsSubtype<SessionStatus, SessionStatusValues>
>;

export interface IMDSSessionProps {
  sessionId: string;
  /**
   * The session title
   */
  title?: string;
  /**
   * Current status of the session
   */
  status: SessionStatus | null;
  /**
   * Array of session events to display
   */
  events: SessionEvent[];
  /**
   * Whether the session starts collapsed
   */
  initiallyCollapsed?: boolean;
  /**
   * Custom icon to display in the session header
   */
  iconSrc?: string;
  /**
   * Debug information to display (optional)
   */
  debugModeEnabled?: boolean;
  /**
   * Height of the container
   */
  containerHeight?: number;
}

export interface SessionEventProps {
  event: SessionEvent;
}

export interface IMDSGroupedMessage {
  type: "single" | "reasoning_group";
  event?: SessionEvent;
  events?: SessionEvent[];
}

export interface SessionPartWithContext {
  part: AnySessionPart;
  context: SessionPartContext;
}

export enum SessionContextKind {
  UserContextV1 = "USER_CONTEXT_V1",
}

export enum SessionContextMentionKind {
  Note = "NOTE",
  Collection = "COLLECTION",
}

export type SessionContextMention =
  | components["schemas"]["NoteSessionMention"]
  | components["schemas"]["CollectionSessionMention"];
